<template>
  <div class="flex flex-col min-h-screen">
    <main class="flex-1 py-12 md:py-24 lg:py-32">
      <div class="container px-4 md:px-6 mx-auto max-w-4xl">
        <!-- Header -->
        <div class="mb-8 text-center">
          <h1 class="text-4xl font-heading font-bold tracking-tighter sm:text-5xl text-graphite dark:text-white mb-4">
            {{ $t('contact.title') }}
          </h1>
          <p class="text-lg font-body text-graphite dark:text-gray-light max-w-2xl mx-auto">
            {{ $t('contact.description') }}
          </p>
        </div>

        <!-- Contact Form and Info -->
        <div class="grid gap-8 md:grid-cols-2 mt-12">
          <!-- Contact Form -->
          <div class="bg-white dark:bg-graphite-light rounded-lg p-6 md:p-8 border border-gray-light dark:border-graphite-light">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mb-6">
              {{ $t('contact.form.title') }}
            </h2>
            <form @submit.prevent="handleSubmit" class="space-y-6">
              <div>
                <label for="name" class="block text-sm font-body font-medium text-graphite dark:text-gray-light mb-2">
                  {{ $t('contact.form.name') }}
                </label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  required
                  class="w-full px-4 py-2 rounded-md border border-gray-light dark:border-graphite-light bg-white dark:bg-graphite text-graphite dark:text-white focus:outline-none focus:ring-2 focus:ring-primary font-body"
                  :placeholder="$t('contact.form.namePlaceholder')"
                />
              </div>
              <div>
                <label for="email" class="block text-sm font-body font-medium text-graphite dark:text-gray-light mb-2">
                  {{ $t('contact.form.email') }}
                </label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  required
                  class="w-full px-4 py-2 rounded-md border border-gray-light dark:border-graphite-light bg-white dark:bg-graphite text-graphite dark:text-white focus:outline-none focus:ring-2 focus:ring-primary font-body"
                  :placeholder="$t('contact.form.emailPlaceholder')"
                />
              </div>
              <div>
                <label for="subject" class="block text-sm font-body font-medium text-graphite dark:text-gray-light mb-2">
                  {{ $t('contact.form.subject') }}
                </label>
                <input
                  id="subject"
                  v-model="form.subject"
                  type="text"
                  required
                  class="w-full px-4 py-2 rounded-md border border-gray-light dark:border-graphite-light bg-white dark:bg-graphite text-graphite dark:text-white focus:outline-none focus:ring-2 focus:ring-primary font-body"
                  :placeholder="$t('contact.form.subjectPlaceholder')"
                />
              </div>
              <div>
                <label for="message" class="block text-sm font-body font-medium text-graphite dark:text-gray-light mb-2">
                  {{ $t('contact.form.message') }}
                </label>
                <textarea
                  id="message"
                  v-model="form.message"
                  required
                  rows="6"
                  class="w-full px-4 py-2 rounded-md border border-gray-light dark:border-graphite-light bg-white dark:bg-graphite text-graphite dark:text-white focus:outline-none focus:ring-2 focus:ring-primary font-body resize-none"
                  :placeholder="$t('contact.form.messagePlaceholder')"
                ></textarea>
              </div>
              <button
                type="submit"
                :disabled="isSubmitting"
                class="w-full px-6 py-3 bg-primary hover:bg-primary-dark text-graphite font-body font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ isSubmitting ? $t('contact.form.sending') : $t('contact.form.submit') }}
              </button>
              <p v-if="submitMessage" :class="submitSuccess ? 'text-primary' : 'text-red-500'" class="text-sm font-body text-center">
                {{ submitMessage }}
              </p>
            </form>
          </div>

          <!-- Contact Information -->
          <div class="space-y-6">
            <div class="bg-white dark:bg-graphite-light rounded-lg p-6 md:p-8 border border-gray-light dark:border-graphite-light">
              <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mb-6">
                {{ $t('contact.info.title') }}
              </h2>
              <div class="space-y-4">
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0">
                    <MailIcon class="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 class="font-heading font-semibold text-graphite dark:text-white mb-1">
                      {{ $t('contact.info.email.title') }}
                    </h3>
                    <a 
                      href="mailto:contacto@zerotoultracycling.com" 
                      class="font-body text-graphite dark:text-gray-light hover:text-primary transition-colors"
                    >
                      contacto@zerotoultracycling.com
                    </a>
                  </div>
                </div>
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0">
                    <MapPinIcon class="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 class="font-heading font-semibold text-graphite dark:text-white mb-1">
                      {{ $t('contact.info.location.title') }}
                    </h3>
                    <p class="font-body text-graphite dark:text-gray-light">
                      {{ $t('contact.info.location.description') }}
                    </p>
                  </div>
                </div>
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0">
                    <ClockIcon class="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 class="font-heading font-semibold text-graphite dark:text-white mb-1">
                      {{ $t('contact.info.response.title') }}
                    </h3>
                    <p class="font-body text-graphite dark:text-gray-light">
                      {{ $t('contact.info.response.description') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Social Media -->
            <div class="bg-white dark:bg-graphite-light rounded-lg p-6 md:p-8 border border-gray-light dark:border-graphite-light">
              <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mb-4">
                {{ $t('contact.social.title') }}
              </h2>
              <p class="font-body text-graphite dark:text-gray-light mb-4">
                {{ $t('contact.social.description') }}
              </p>
              <div class="flex gap-4">
                <!-- Aquí puedes añadir enlaces a redes sociales cuando los tengas -->
                <a 
                  href="#" 
                  class="px-4 py-2 bg-gray-light dark:bg-graphite text-graphite dark:text-white rounded-md hover:bg-primary hover:text-graphite transition-colors font-body text-sm"
                >
                  Instagram
                </a>
                <a 
                  href="#" 
                  class="px-4 py-2 bg-gray-light dark:bg-graphite text-graphite dark:text-white rounded-md hover:bg-primary hover:text-graphite transition-colors font-body text-sm"
                >
                  Twitter
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Back to home button -->
        <div class="mt-12 pt-8 border-t border-gray-light dark:border-graphite-light text-center">
          <router-link 
            to="/" 
            class="inline-flex items-center px-6 py-3 bg-primary hover:bg-primary-dark text-graphite font-body font-medium rounded-md transition-colors"
          >
            {{ $t('contact.backToHome') }}
          </router-link>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Mail as MailIcon, MapPin as MapPinIcon, Clock as ClockIcon } from 'lucide-vue-next'

const { t } = useI18n()

const form = ref({
  name: '',
  email: '',
  subject: '',
  message: ''
})

const isSubmitting = ref(false)
const submitMessage = ref('')
const submitSuccess = ref(false)

const handleSubmit = async () => {
  isSubmitting.value = true
  submitMessage.value = ''
  
  // Simulación de envío (en producción, aquí harías una llamada a tu API)
  try {
    // Aquí iría la lógica real de envío
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    submitSuccess.value = true
    submitMessage.value = t('contact.form.success')
    
    // Limpiar formulario
    form.value = {
      name: '',
      email: '',
      subject: '',
      message: ''
    }
    
    // Limpiar mensaje después de 5 segundos
    setTimeout(() => {
      submitMessage.value = ''
    }, 5000)
  } catch (error) {
    submitSuccess.value = false
    submitMessage.value = t('contact.form.error')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.container {
  max-width: 1200px;
}
</style>
